/**
 * Storage-quota helpers: `navigator.storage.persist()`, `estimate()`, and a
 * `storage-low` event when usage crosses the configured water mark.
 *
 * All functions degrade gracefully: environments without the StorageManager
 * API (older Safari, SSR) return conservative zeros instead of throwing.
 */

import { warn } from './error-codes'
import type { QuotaEstimate } from './types'

function getStorageManager(): StorageManager | null {
	const nav = (globalThis as { navigator?: Navigator }).navigator
	const sm = nav?.storage
	return sm && typeof sm.estimate === 'function' ? sm : null
}

/**
 * Request durable (non-evictable) storage. Resolves to the granted state;
 * `false` when unsupported or denied — callers should treat best-effort
 * persistence as the default rather than a hard failure.
 */
export async function requestPersistence(): Promise<boolean> {
	const sm = getStorageManager()
	if (!sm || typeof sm.persist !== 'function') return false
	try {
		return await sm.persist()
	} catch {
		return false
	}
}

/** True when storage is already durable; false when unknown/unsupported. */
export async function isPersisted(): Promise<boolean> {
	const sm = getStorageManager()
	if (!sm || typeof sm.persisted !== 'function') return false
	try {
		return await sm.persisted()
	} catch {
		return false
	}
}

/**
 * Best-effort usage estimate. `ratio`/`low` are derived; a structured
 * `WEB_OFFLINE_STORE_QUOTA_LOW` warning is emitted once `ratio >= waterMark`.
 */
export async function estimateQuota(waterMark: number): Promise<QuotaEstimate> {
	const sm = getStorageManager()
	if (!sm) {
		return { usage: 0, quota: 0, ratio: 0, low: false }
	}
	let usage = 0
	let quota = 0
	try {
		const est = await sm.estimate()
		usage = est.usage ?? 0
		quota = est.quota ?? 0
	} catch {
		return { usage: 0, quota: 0, ratio: 0, low: false }
	}
	const ratio = quota > 0 ? Math.min(1, Math.max(0, usage / quota)) : 0
	const low = quota > 0 && ratio >= waterMark
	if (low) {
		warn({
			fn: 'estimateQuota',
			code: 'WEB_OFFLINE_STORE_QUOTA_LOW',
			message: `storage usage ${(ratio * 100).toFixed(1)}% >= water mark ${(
				waterMark * 100
			).toFixed(0)}%`,
		})
	}
	return { usage, quota, ratio, low }
}
